import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const EMPTY_FORM = { ftp: "", date: "", notes: "" };

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const inputStyle = {
    background: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    padding: "8px 10px",
    fontSize: "13px",
    width: "100%",
    boxSizing: "border-box",
};

const labelStyle = {
    fontSize: "12px",
    color: "#888",
    display: "block",
    marginBottom: "4px",
};

const sectionLabel = {
    fontSize: "11px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "12px",
};

function formatDate(str) {
    if (!str) return "—";
    const [y, m, d] = str.split("-");
    return `${m}/${d}/${y}`;
}

const FtpLog = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showZwiftSearch, setShowZwiftSearch] = useState(false);
    const [zwiftSearch, setZwiftSearch] = useState("");
    const [zwiftResults, setZwiftResults] = useState([]);
    const [zwiftLoading, setZwiftLoading] = useState(false);
    const [zwiftSearched, setZwiftSearched] = useState(false);

    const getToken = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const token = await getToken();
            const { data } = await axios.get(`${base_url}/ftp`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLogs(data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditId(null);
        setError(null);
        setShowForm(true);
    };

    const openEdit = (log) => {
        setForm({ ftp: String(log.ftp), date: log.date || "", notes: log.notes || "" });
        setEditId(log._id);
        setError(null);
        setShowForm(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getToken();
            if (editId) {
                await axios.put(`${base_url}/ftp/${editId}`, form, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${base_url}/ftp`, form, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            setShowForm(false);
            fetchLogs();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this entry?")) return;
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/ftp/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLogs(prev => prev.filter(l => l._id !== id));
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const searchZwiftActivities = async () => {
        setZwiftLoading(true);
        setZwiftSearched(true);
        try {
            const token = await getToken();
            const params = zwiftSearch.trim() ? `?search=${encodeURIComponent(zwiftSearch.trim())}` : "";
            const { data } = await axios.get(`${base_url}/activity/ftp-candidates${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setZwiftResults(data.activities || []);
        } catch (err) {
            setZwiftResults([]);
        } finally {
            setZwiftLoading(false);
        }
    };

    const maxFtp = logs.length > 0 ? Math.max(...logs.map(l => l.ftp)) : null;

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "720px", margin: "0 auto" }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <div>
                        <h2 style={{ color: "#4ecdc4", margin: 0 }}>FTP Log</h2>
                        {maxFtp !== null && (
                            <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                                All-time best: <span style={{ color: "#4ecdc4", fontWeight: "bold" }}>{maxFtp}W</span>
                            </div>
                        )}
                    </div>
                    {!showForm && (
                        <button
                            onClick={openCreate}
                            style={{
                                padding: "8px 18px",
                                background: "#4ecdc4",
                                color: "#0d0d1a",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "13px",
                            }}
                        >
                            + Log FTP
                        </button>
                    )}
                </div>

                {showForm && (
                    <div style={{ ...card, marginBottom: "24px" }}>
                        <div style={{ ...sectionLabel, marginBottom: "16px" }}>
                            {editId ? "Edit Entry" : "New FTP Entry"}
                        </div>
                        <form onSubmit={handleSave}>
                            <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                <div style={{ flex: "0 0 140px" }}>
                                    <label style={labelStyle}>FTP (watts)</label>
                                    <input
                                        type="number"
                                        style={inputStyle}
                                        value={form.ftp}
                                        onChange={e => handleChange("ftp", e.target.value)}
                                        placeholder="e.g. 280"
                                        min="1"
                                        required
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>Date</label>
                                    <input
                                        type="date"
                                        style={inputStyle}
                                        value={form.date}
                                        onChange={e => handleChange("date", e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                                <label style={labelStyle}>Notes</label>
                                <textarea
                                    rows={3}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    value={form.notes}
                                    onChange={e => handleChange("notes", e.target.value)}
                                    placeholder="Optional notes (test type, conditions, etc.)"
                                />
                            </div>

                            {error && (
                                <div style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{error}</div>
                            )}

                            <div style={{ display: "flex", gap: "10px" }}>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        padding: "8px 20px",
                                        background: saving ? "#2a2a4a" : "#4ecdc4",
                                        color: saving ? "#555" : "#0d0d1a",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: saving ? "not-allowed" : "pointer",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                    }}
                                >
                                    {saving ? "Saving..." : editId ? "Save Changes" : "Add Entry"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowForm(false); setEditId(null); setError(null); }}
                                    style={{
                                        padding: "8px 16px",
                                        background: "transparent",
                                        color: "#555",
                                        border: "1px solid #2a2a4a",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div style={{ marginBottom: "24px" }}>
                    <button
                        onClick={() => setShowZwiftSearch(v => !v)}
                        style={{
                            background: "transparent",
                            border: "1px solid #2a2a4a",
                            borderRadius: "6px",
                            color: "#888",
                            cursor: "pointer",
                            fontSize: "12px",
                            padding: "6px 14px",
                        }}
                    >
                        {showZwiftSearch ? "Hide" : "Search Zwift Activities"}
                    </button>
                    {showZwiftSearch && (
                        <div style={{ ...card, marginTop: "12px" }}>
                            <div style={sectionLabel}>Zwift FTP Candidates</div>
                            <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                                <input
                                    type="text"
                                    style={{ ...inputStyle, flex: 1, minWidth: "160px" }}
                                    placeholder='Filter by name (e.g. "FTP" or leave blank for all)'
                                    value={zwiftSearch}
                                    onChange={e => setZwiftSearch(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && searchZwiftActivities()}
                                />
                                <button
                                    onClick={searchZwiftActivities}
                                    disabled={zwiftLoading}
                                    style={{
                                        padding: "8px 16px",
                                        background: zwiftLoading ? "#2a2a4a" : "#4ecdc4",
                                        color: zwiftLoading ? "#555" : "#0d0d1a",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: zwiftLoading ? "not-allowed" : "pointer",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {zwiftLoading ? "Searching..." : "Search"}
                                </button>
                            </div>
                            {zwiftSearched && !zwiftLoading && (
                                zwiftResults.length === 0 ? (
                                    <div style={{ color: "#555", fontSize: "13px" }}>No matching Zwift activities found.</div>
                                ) : (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                                            <thead>
                                                <tr style={{ color: "#555", textAlign: "left" }}>
                                                    <th style={{ paddingBottom: "8px", fontWeight: "normal" }}>Date</th>
                                                    <th style={{ paddingBottom: "8px", fontWeight: "normal" }}>Name</th>
                                                    <th style={{ paddingBottom: "8px", fontWeight: "normal", textAlign: "right" }}>FTP</th>
                                                    <th style={{ paddingBottom: "8px", fontWeight: "normal", textAlign: "right" }}>Max Watts</th>
                                                    <th style={{ paddingBottom: "8px", fontWeight: "normal", textAlign: "right" }}>Duration</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {zwiftResults.map(a => {
                                                    const date = a.start_date ? new Date(a.start_date) : null;
                                                    const dateStr = date
                                                        ? `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
                                                        : "—";
                                                    const mins = a.moving_time ? Math.round(a.moving_time / 60) : null;
                                                    const ftpExact = a.average_watts ? Math.round(a.average_watts * 0.95) : null;
                                                    const ftpEstimate = !ftpExact && a.max_watts ? Math.round(a.max_watts * 0.76) : null;
                                                    return (
                                                        <tr key={a.id} style={{ borderTop: "1px solid #1e1e3a" }}>
                                                            <td style={{ padding: "7px 0", color: "#666" }}>{dateStr}</td>
                                                            <td style={{ padding: "7px 8px", color: "#bbb" }}>{a.name || "—"}</td>
                                                            <td style={{ padding: "7px 0", textAlign: "right", color: ftpExact ? "#4ecdc4" : "#3a8a84", fontWeight: "bold" }}>
                                                                {ftpExact ? `${ftpExact}W` : ftpEstimate ? `~${ftpEstimate}W` : "—"}
                                                            </td>
                                                            <td style={{ padding: "7px 0", textAlign: "right", color: "#888" }}>
                                                                {a.max_watts ? `${a.max_watts}W` : "—"}
                                                            </td>
                                                            <td style={{ padding: "7px 0", textAlign: "right", color: "#666" }}>
                                                                {mins ? `${mins}m` : "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>Loading...</div>
                ) : logs.length === 0 ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>No FTP entries yet. Add one above.</div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {logs.map((log, i) => {
                            const prev = logs[i + 1];
                            const delta = prev ? log.ftp - prev.ftp : null;
                            const isPr = log.ftp === maxFtp;
                            return (
                                <div key={log._id} style={card}>
                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                                <span style={{ fontSize: "22px", fontWeight: "bold", color: "#4ecdc4" }}>
                                                    {log.ftp}W
                                                </span>
                                                {isPr && (
                                                    <span style={{
                                                        fontSize: "10px",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        border: "1px solid #4ecdc4",
                                                        color: "#4ecdc4",
                                                    }}>
                                                        PR
                                                    </span>
                                                )}
                                                {delta !== null && (
                                                    <span style={{
                                                        fontSize: "12px",
                                                        color: delta > 0 ? "#4ade80" : delta < 0 ? "#f87171" : "#555",
                                                    }}>
                                                        {delta > 0 ? `+${delta}` : delta}W
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                                                {formatDate(log.date)}
                                            </div>
                                            {log.notes && (
                                                <div style={{ fontSize: "12px", color: "#666", marginTop: "6px", fontStyle: "italic" }}>
                                                    {log.notes}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                                            <button
                                                onClick={() => openEdit(log)}
                                                style={{
                                                    padding: "5px 12px",
                                                    background: "transparent",
                                                    color: "#4ecdc4",
                                                    border: "1px solid #4ecdc4",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(log._id)}
                                                style={{
                                                    padding: "5px 12px",
                                                    background: "transparent",
                                                    color: "#f87171",
                                                    border: "1px solid #4a2a2a",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FtpLog;
